<template>
  <div class="rb-screening-p1">
    <p v-if="promptText" class="rb-ob-q">{{ promptText }}</p>
    <p class="rb-ob-q-hint">{{ $t('intake.topicsHelp', {max: maxTopicsLabel}) }}</p>
    <div class="rb-ob-counter-slot" aria-live="polite">
      <transition name="rb-ob-swap" mode="out-in">
        <p :key="counterLabel" class="rb-ob-counter">{{ counterLabel }}</p>
      </transition>
    </div>

    <div class="rb-ob-topic-grid">
      <section
          v-for="group in topicGroups"
          :key="group.id"
          class="rb-ob-topic-group"
          :class="{ 'is-collapsed': isCollapsed(group.id) }"
      >
        <div
            class="rb-ob-topic-group-head"
            role="button"
            tabindex="0"
            :aria-expanded="isCollapsed(group.id) ? 'false' : 'true'"
            @click="toggleGroup(group.id)"
            @keydown.enter.prevent="toggleGroup(group.id)"
            @keydown.space.prevent="toggleGroup(group.id)"
        >
          <span class="rb-ob-group-icon" aria-hidden="true" v-html="iconFor(group.id)"></span>
          <h2 class="rb-ob-topic-group-title">{{ group.label }}</h2>
          <span class="rb-ob-topic-group-meta">
            <span class="rb-ob-topic-group-count" aria-hidden="true">{{ groupCountLabel(group) }}</span>
            <span class="rb-ob-topic-group-toggle" aria-hidden="true">+</span>
          </span>
        </div>
        <div class="rb-ob-topic-chips-wrap">
          <div class="rb-ob-topic-chips">
            <label
                v-for="chip in group.chips"
                :key="chip.id"
                class="rb-ob-topic-chip"
                :class="{ 'is-selected': isSelected(chip.id), 'is-disabled': unsure }"
            >
              <input
                  type="checkbox"
                  :value="chip.id"
                  :checked="isSelected(chip.id)"
                  :disabled="unsure"
                  @change="toggleTopic(chip.id, $event)"
              >
              <span class="rb-ob-choice-body">
                <span class="rb-ob-choice-title">{{ chip.label || chip.id }}</span>
              </span>
            </label>
          </div>
        </div>
      </section>

      <label
          v-if="unsureOption"
          class="rb-ob-topic-chip rb-ob-topic-chip--unsure"
          :class="{ 'is-selected': unsure }"
      >
        <input
            type="checkbox"
            :value="unsureOption.id"
            :checked="unsure"
            @change="toggleUnsure"
        >
        <span class="rb-ob-choice-body">
          <span class="rb-ob-choice-title">{{ unsureOption.label || unsureOption.id }}</span>
          <span class="rb-ob-choice-hint">{{ unsureHint }}</span>
        </span>
      </label>
    </div>
    <p v-if="maxError" class="rb-ob-error" role="alert">{{ $t('intake.topicsMax') }}</p>
  </div>
</template>

<script>
import {groupConcernOptions, P1_MAX_TOPICS} from '@/intake/screeningConcernGroups'
import {toFaDigits} from '@/intake/intakeState'

const GROUP_ICONS = [
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8.5 14.5c1.2 1.2 2.6 1.8 3.5 1.8s2.3-.6 3.5-1.8"/><circle cx="9" cy="10" r=".75" fill="currentColor" stroke="none"/><circle cx="15" cy="10" r=".75" fill="currentColor" stroke="none"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l9 16H3L12 3z"/><path d="M12 10v4"/><circle cx="12" cy="16.5" r=".75" fill="currentColor" stroke="none"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/><path d="M9.5 11.5l5 5M14.5 11.5l-5 5"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="8.5" cy="8" r="3"/><circle cx="15.5" cy="8" r="3"/><path d="M3.5 19c.4-3 2.6-5 5-5h1c1 .2 1.8.7 2.5 1.4.7-.7 1.5-1.2 2.5-1.4h1c2.4 0 4.6 2 5 5"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11l8-7 8 7"/><path d="M6 10.5V20h12v-9.5"/><path d="M10 20v-5h4v5"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8h16v11H4z"/><path d="M9 8V6.5A1.5 1.5 0 0 1 10.5 5h3A1.5 1.5 0 0 1 15 6.5V8"/><path d="M4 12h16"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M15.5 3.5A7.5 7.5 0 1 0 20.5 14 6 6 0 0 1 15.5 3.5z"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M17 7a5 5 0 0 0-8.5-2.5L7 6"/><path d="M7 3v3h3"/><path d="M7 17a5 5 0 0 0 8.5 2.5L17 18"/><path d="M17 21v-3h-3"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="3.25"/><path d="M5.5 19.5c1.2-3.2 3.5-4.8 6.5-4.8s5.3 1.6 6.5 4.8"/><circle cx="12" cy="12" r="9"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-7-4.4-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 11c0 5.6-7 10-7 10z"/><path d="M8.5 12.5h2l1.2-2.5 1.6 5 1.2-2.5H16"/></svg>',
]

export default {
  name: 'ScreeningTopicsSelect',
  props: {
    question: {
      type: Object,
      required: true,
    },
    serviceTrackId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selected: [],
      collapsed: {},
      maxError: '',
    }
  },
  computed: {
    promptText() {
      return (this.question && this.question.text) || ''
    },
    options() {
      return (this.question && Array.isArray(this.question.options))
        ? this.question.options.filter((o) => o && o.id)
        : []
    },
    maxTopics() {
      const max = this.question && this.question.max != null
        ? Number(this.question.max)
        : P1_MAX_TOPICS
      return Number.isFinite(max) && max > 0 ? max : P1_MAX_TOPICS
    },
    maxTopicsLabel() {
      return this.$route && this.$route.params.lang === 'en' ? String(this.maxTopics) : toFaDigits(this.maxTopics)
    },
    trackId() {
      return this.serviceTrackId
        || (this.question && this.question.service_track_id)
        || null
    },
    serverGroups() {
      const raw = this.question && Array.isArray(this.question.option_groups)
        ? this.question.option_groups
        : []
      return raw
        .filter((g) => g && Array.isArray(g.options) && g.options.length)
        .map((g) => ({
          id: g.id || g.label,
          label: g.label || g.id,
          chips: g.options.filter((o) => o && o.id && o.id !== 'UNSURE'),
        }))
        .filter((g) => g.chips.length)
    },
    grouped() {
      if (this.serverGroups.length) {
        return {
          groups: this.serverGroups,
          unsureOption: (this.question && this.question.unsure_option)
            || this.options.find((o) => o.id === 'UNSURE')
            || null,
          track: this.trackId || 'server',
        }
      }
      const grouped = groupConcernOptions(this.options, this.trackId)
      return {
        ...grouped,
        unsureOption: (this.question && this.question.unsure_option) || grouped.unsureOption,
      }
    },
    topicGroups() {
      return this.grouped.groups
    },
    unsureOption() {
      const option = this.grouped.unsureOption
      return option && option.id === 'UNSURE' ? option : null
    },
    selectableIds() {
      const ids = []
      this.topicGroups.forEach(group => group.chips.forEach(chip => ids.push(chip.id)))
      if (this.unsureOption) ids.push(this.unsureOption.id)
      return ids
    },
    unsure() {
      return this.selected.length === 1 && this.selected[0] === 'UNSURE'
    },
    selectedNeeds() {
      return this.selected.filter((id) => id !== 'UNSURE')
    },
    unsureHint() {
      try {
        return this.$t('intake.topicsUnsureHint')
      } catch (e) {
        return ''
      }
    },
    counterLabel() {
      if (this.unsure) {
        try {
          return this.$t('intake.topicsUnsurePath')
        } catch (e) {
          return 'UNSURE'
        }
      }
      try {
        return this.$t('intake.topicsCount', {
          count: toFaDigits(this.selectedNeeds.length),
        })
      } catch (e) {
        return String(this.selectedNeeds.length)
      }
    },
  },
  watch: {
    question: {
      immediate: true,
      handler() {
        const prefill = (((this.question || {}).answer_prefill || {}).answer_ids)
        this.selected = Array.isArray(prefill) ? prefill.filter(id => this.selectableIds.indexOf(id) !== -1) : []
        this.maxError = ''
        const collapsed = {}
        this.topicGroups.forEach((group) => {
          collapsed[group.id] = false
        })
        this.collapsed = collapsed
        this.emitChange()
      },
    },
  },
  methods: {
    toFaDigits,
    iconFor(groupId) {
      let hash = 0
      String(groupId || '').split('').forEach((ch) => {
        hash = (hash + ch.charCodeAt(0)) % GROUP_ICONS.length
      })
      return GROUP_ICONS[hash] || GROUP_ICONS[0]
    },
    isCollapsed(groupId) {
      return !!this.collapsed[groupId]
    },
    isSelected(id) {
      return this.selected.indexOf(id) !== -1
    },
    groupCountLabel(group) {
      const n = group.chips.filter((chip) => this.isSelected(chip.id)).length
      return n ? toFaDigits(n) : ''
    },
    toggleGroup(groupId) {
      this.$set(this.collapsed, groupId, !this.collapsed[groupId])
    },
    collapseOthers(activeId) {
      this.topicGroups.forEach((group) => {
        this.$set(this.collapsed, group.id, group.id !== activeId)
      })
    },
    collapseAll() {
      this.topicGroups.forEach((group) => {
        this.$set(this.collapsed, group.id, true)
      })
    },
    toggleUnsure() {
      if (this.unsure) {
        this.selected = []
        this.topicGroups.forEach((group) => {
          this.$set(this.collapsed, group.id, false)
        })
      } else {
        this.selected = ['UNSURE']
        this.collapseAll()
      }
      this.maxError = ''
      this.emitChange()
    },
    toggleTopic(id, event) {
      if (this.unsure) {
        return
      }
      const index = this.selected.indexOf(id)
      if (index !== -1) {
        this.selected.splice(index, 1)
        this.maxError = ''
        this.emitChange()
        return
      }
      if (this.selectedNeeds.length >= this.maxTopics) {
        // A rejected native change does not trigger a Vue checked-property diff.
        // Restore the actual input as well as keeping the model within its limit.
        if (event && event.target) event.target.checked = false
        this.maxError = 'max'
        this.emitChange()
        return
      }
      this.selected.push(id)
      this.maxError = ''
      const active = this.topicGroups.find(
        (group) => group.chips.some((chip) => chip.id === id),
      )
      if (active) {
        this.collapseOthers(active.id)
      }
      this.emitChange()
    },
    buildPayload() {
      const required = !(this.question && this.question.required === false)
      const ids = this.selected.filter(id => this.selectableIds.indexOf(id) !== -1)
      const overMax = !this.unsure && this.selectedNeeds.length > this.maxTopics
      return {
        valid: this.topicGroups.length > 0 && !overMax && (!required || ids.length > 0),
        answer_ids: ids,
      }
    },
    emitChange() {
      this.$emit('change', this.buildPayload())
    },
    getAnswer() {
      return this.buildPayload()
    },
  },
}
</script>
